'use client';
import React, { useState, useEffect, useRef } from 'react';

interface FormData {
  name: string;
  email: string;
  company: string;
  serviceInterest: string;
  message: string;
}

const SERVICE_OPTIONS = [
  'Custom Branded Merchandise',
  'QR-Enabled Smart Swag',
  'Corporate Gifting Program',
  'Event & Trade Show Swag',
  'Employee Onboarding Kits',
  'Other / Not Sure Yet',
];

export default function CTASection() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    serviceInterest: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );

    const animatedEls = sectionRef.current?.querySelectorAll('.animate-cta');
    animatedEls?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.email || !formData.serviceInterest) {
      setError('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/submit-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!res.ok) {
        throw new Error('Submission failed');
      }

      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setLoading(false);
    }
  };

  const inputBase =
    'w-full font-inter text-white placeholder-gray-500 rounded px-4 py-3 outline-none transition-all duration-200';
  const inputStyle = {
    backgroundColor: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(0, 180, 255, 0.3)',
    fontSize: '15px',
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    (e.currentTarget as HTMLElement).style.borderColor = '#ff6b35';
  };
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0, 180, 255, 0.3)';
  };

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="py-24 px-4 relative overflow-hidden"
      style={{
        backgroundImage: 'radial-gradient(#0a2a3d 0%, #050d18 60%, #020608 100%)',
      }}
    >
      {/* Background decorations */}
      <div
        className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #00b4ff 0%, transparent 70%)' }}
      />
      <div
        className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #00ff88 0%, transparent 70%)' }}
      />

      <div className="max-w-3xl mx-auto text-center relative z-10">
        <p
          className="animate-cta opacity-0 translate-y-8 transition-all duration-700 font-montserrat font-bold uppercase mb-4"
          style={{ color: '#ff6b35', letterSpacing: '3px', fontSize: '14px' }}
        >
          Ready to Get Serious?
        </p>
        <h2
          className="animate-cta opacity-0 translate-y-8 transition-all duration-700 font-montserrat font-black text-white mb-6"
          style={{
            fontSize: 'clamp(28px, 5vw, 56px)',
            letterSpacing: '-1px',
            transitionDelay: '100ms',
          }}
        >
          Stop Leaving Brand Leverage{' '}
          <span style={{ color: '#ff6b35' }}>On The Table</span>
        </h2>
        <p
          className="animate-cta opacity-0 translate-y-8 transition-all duration-700 mb-10 leading-relaxed"
          style={{
            color: 'rgb(136, 147, 152)',
            fontSize: '18px',
            transitionDelay: '150ms',
          }}
        >
          Every day without premium tactile branding is a day your competitors gain ground. Tell us
          about your project and we&apos;ll get back to you fast.
        </p>

        {/* Lead Capture Form */}
        <div
          className="animate-cta opacity-0 translate-y-8 transition-all duration-700 mb-8"
          style={{ transitionDelay: '200ms' }}
        >
          {!submitted ? (
            <form
              onSubmit={handleSubmit}
              className="text-left rounded-xl p-6 sm:p-8 space-y-4"
              style={{
                backgroundColor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(0, 180, 255, 0.15)',
              }}
            >
              <p
                className="font-montserrat font-bold uppercase text-white text-center mb-6"
                style={{ letterSpacing: '2px', fontSize: '13px' }}
              >
                Get a Quote — It&apos;s Free
              </p>

              {/* Row 1: Name + Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-montserrat font-bold uppercase text-gray-400 mb-1" style={{ letterSpacing: '1px' }}>
                    Name <span style={{ color: '#ff6b35' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder="Jane Smith"
                    required
                    className={inputBase}
                    style={{ ...inputStyle }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-montserrat font-bold uppercase text-gray-400 mb-1" style={{ letterSpacing: '1px' }}>
                    Email <span style={{ color: '#ff6b35' }}>*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder="jane@company.com"
                    required
                    className={inputBase}
                    style={{ ...inputStyle }}
                  />
                </div>
              </div>

              {/* Row 2: Company + Service Interest */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-montserrat font-bold uppercase text-gray-400 mb-1" style={{ letterSpacing: '1px' }}>
                    Company
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder="Acme Corp"
                    className={inputBase}
                    style={{ ...inputStyle }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-montserrat font-bold uppercase text-gray-400 mb-1" style={{ letterSpacing: '1px' }}>
                    Service Interest <span style={{ color: '#ff6b35' }}>*</span>
                  </label>
                  <select
                    name="serviceInterest"
                    value={formData.serviceInterest}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    required
                    className={inputBase}
                    style={{ ...inputStyle, cursor: 'pointer' }}
                  >
                    <option value="" disabled style={{ backgroundColor: '#0a192f' }}>
                      Select a service…
                    </option>
                    {SERVICE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt} style={{ backgroundColor: '#0a192f' }}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 3: Message */}
              <div>
                <label className="block text-xs font-montserrat font-bold uppercase text-gray-400 mb-1" style={{ letterSpacing: '1px' }}>
                  Tell us more (optional)
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  placeholder="Budget, timeline, quantity, or anything else we should know…"
                  rows={3}
                  className={inputBase}
                  style={{ ...inputStyle, resize: 'none' }}
                />
              </div>

              {error && (
                <p className="text-sm text-center" style={{ color: '#ff6b35' }}>
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full font-montserrat font-bold uppercase text-white rounded px-8 py-4 transition-all duration-300 mt-2"
                style={{
                  backgroundColor: loading ? 'rgba(255,107,53,0.6)' : '#ff6b35',
                  border: '2px solid #ff6b35',
                  letterSpacing: '0.4px',
                  fontSize: '14px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                    (e.currentTarget as HTMLElement).style.color = '#ff6b35';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    (e.currentTarget as HTMLElement).style.backgroundColor = '#ff6b35';
                    (e.currentTarget as HTMLElement).style.color = '#ffffff';
                  }
                }}
              >
                {loading ? 'Sending…' : 'Request a Quote →'}
              </button>
            </form>
          ) : (
            <div
              className="rounded-xl px-8 py-10 flex flex-col items-center gap-4"
              style={{
                backgroundColor: 'rgba(0, 255, 136, 0.06)',
                border: '1px solid rgba(0, 255, 136, 0.3)',
              }}
            >
              <span style={{ color: '#00ff88', fontSize: '40px' }}>✓</span>
              <p className="font-montserrat font-black text-white text-xl">
                We&apos;ve got your request!
              </p>
              <p style={{ color: 'rgb(136, 147, 152)', fontSize: '16px' }}>
                Expect to hear from us within one business day.
              </p>
            </div>
          )}
        </div>

        {/* Secondary CTAs */}
        <div
          className="animate-cta opacity-0 translate-y-8 transition-all duration-700 flex flex-col sm:flex-row items-center justify-center gap-4"
          style={{ transitionDelay: '300ms' }}
        >
          <a
            href="#product-suite"
            className="inline-flex items-center gap-2 font-montserrat font-bold uppercase text-white rounded px-8 py-4 transition-all duration-300"
            style={{
              border: '2px solid rgba(0, 180, 255, 0.5)',
              letterSpacing: '0.4px',
              fontSize: '14px',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = '#ff6b35';
              (e.currentTarget as HTMLElement).style.color = '#ff6b35';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0, 180, 255, 0.5)';
              (e.currentTarget as HTMLElement).style.color = '#ffffff';
            }}
          >
            Explore All 3 Suites
          </a>
          <a
            href="mailto:swag@seriousleverage.com"
            className="inline-flex items-center gap-2 font-montserrat font-bold uppercase text-white rounded px-8 py-4 transition-all duration-300"
            style={{
              border: '2px solid rgba(255,255,255,0.2)',
              letterSpacing: '0.4px',
              fontSize: '14px',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.5)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.2)';
            }}
          >
            Email Us Directly
          </a>
        </div>
      </div>
    </section>
  );
}
